// src/registry.ts
var DELEGATED_PATH_SEGMENT = "delegated";
var CONTEXT_REGISTRY = {
  scheme: { pathKey: "schemes", prefixes: ["schemes"], reachable: true },
  ledger: { pathKey: "ledger", prefixes: ["ledger"], reachable: true },
  // /redemptions/public/** is the NONE-auth QR landing lookup (remediation H-12): a printed
  // coupon card encodes {qr-base-url}/r/{code}, and the page that URL opens reads this. Genuinely
  // internet-facing, like partner's name-echo and unlike the in-cluster entries elsewhere on this
  // list — no JWT, no tenant header (the tenant is block 1 of the card number), and a response
  // that names nothing about the holder. Everything else on this context stays JWT-gated.
  redemption: {
    pathKey: "redemptions",
    prefixes: ["redemptions"],
    publicPrefixes: ["redemptions/public"],
    reachable: true
  },
  // `loan` is a SECOND prefix, not a rename: loan-service's reader-facing surface is
  // /loan-leads/** and stays the pathKey, while /loan/internal/** is the S2S pair
  // engagement-service reads the financing consent and product terms from (WP-7, WP-8).
  //
  // It is here because this list derives the springdoc group names the snapshot fetcher
  // asks for: without it both internal routes are absent from .openapi/loan.json, and so
  // from every gate that reads it — which is why rest-binding-exceptions.json carried two
  // waivers on those exact routes, each naming "run openapi:snapshot" as its exit condition.
  // `lender-eligibility-rules` is a THIRD prefix on the same pathKey: the rule catalogue is a
  // sibling resource of /loan-leads, not a sub-path, so the loan gateway gives it its own greedy
  // route under the same `loan-leads` mapping key. It was missing here while the routes existed,
  // were deployed and were called — which is what `contract:paths` was reporting as seven
  // invented paths in api-loan's endpoints.ts.
  loan: {
    pathKey: "loan-leads",
    prefixes: ["loan-leads", "loan", "lender-eligibility-rules"],
    reachable: true
  },
  goals: { pathKey: "goals", prefixes: ["goals"], reachable: true },
  network: { pathKey: "network", prefixes: ["network"], reachable: true },
  audit: { pathKey: "audit", prefixes: ["audit"], reachable: true },
  comms: {
    pathKey: "comms",
    // `announcements` is comms-service's only reader-facing prefix — everything
    // else here is SQS-in or staff-read-only. It sits at the root rather than
    // under `notifications` because `AnnouncementsController` is mapped to
    // `/announcements`, not nested under the notification surface.
    //
    // `conversations` (WP-5) is its own prefix for the same reason:
    // ConversationController is mapped to `/conversations`, not nested under the
    // notification surface. This list derives comms' springdoc group names, so
    // without the entry both routes are absent from .openapi/comms.json and from
    // every gate that reads the snapshot.
    //
    // Comments belong HERE and not between the entries below: contextsFromRegistry()
    // in scripts/testing/fetch-openapi.mjs brace-matches this object and reads the
    // array textually, so an inline comment is parsed as a prefix.
    prefixes: [
      "notifications",
      "notification-preferences",
      "templates",
      "channel-providers",
      "announcements",
      "conversations"
    ],
    reachable: true
  },
  helpdesk: { pathKey: "helpdesk", prefixes: ["helpdesk"], reachable: true },
  identity: {
    pathKey: "identity",
    // `delegation` is WP-9's rail: the jeweller's grants and live-session panel, the
    // operator's tenant switcher and token mint, and the second issuer's JWKS. Its own
    // prefix rather than a sub-path of `users`, because a prefix is what the gateway
    // routes, what springdoc groups, and what the OpenAPI snapshot is keyed on.
    prefixes: ["kyc", "me", "users", "delegation"],
    // `auth` is the pre-token login surface. `delegation/.well-known` is the delegation
    // issuer's OIDC discovery document and JWKS — NONE-auth because the thing that reads
    // it is API Gateway's own JWT authorizer, from AWS's network rather than from inside
    // the VPC. It carries a public RSA key and static metadata, and varies by no caller.
    publicPrefixes: ["auth", "delegation/.well-known"],
    reachable: true
  },
  payment: { pathKey: "payments", prefixes: ["payments"], reachable: true },
  compliance: { pathKey: "compliance", prefixes: ["compliance"], reachable: true },
  dispute: { pathKey: "disputes", prefixes: ["disputes"], reachable: true },
  reporting: { pathKey: "reports", prefixes: ["reports"], reachable: true },
  tenant: { pathKey: "tenants", prefixes: ["tenants"], reachable: true },
  pricing: {
    pathKey: "pricing",
    prefixes: ["pricing"],
    publicPrefixes: ["pricing/current"],
    reachable: true
  },
  // partner-service is built (apis/supporting/partner-service), assembled into
  // supporting-bootstrap and mono-bootstrap, and fronted by
  // gateways/rest/supporting/partner-gateway.yaml (multi-prefix: /partners/** is
  // tenant administration, /partner is the agent self-service surface,
  // /commission-plan is the tenant plan singleton). `reachable` is true from
  // that change on; mock mode is unaffected by this flag either way.
  partner: {
    pathKey: "partners",
    prefixes: ["partners", "partner", "commission-plan"],
    // /partners/public/** is the NONE-auth agent-code name echo (design D8): tenant rides in
    // the code's prefix, no header, no JWT; everything else on this context stays JWT-gated.
    publicPrefixes: ["partners/public"],
    reachable: true
  },
  // lender-service is built (apis/supporting/lender-service), assembled into
  // supporting-bootstrap and mono-bootstrap, and fronted by
  // gateways/rest/supporting/lender-gateway.yaml. `reachable` is true from that
  // change on.
  lender: { pathKey: "lenders", prefixes: ["lenders"], reachable: true },
  // crm-service is built (apis/supporting/crm-service), assembled into
  // supporting-bootstrap and mono-bootstrap, and fronted by
  // gateways/rest/supporting/crm-gateway.yaml. `reachable` is true from that change
  // on; mock mode is unaffected by this flag either way.
  crm: { pathKey: "crm", prefixes: ["crm"], reachable: true, delegable: true },
  // campaign-service is built (apis/supporting/campaign-service), assembled into
  // supporting-bootstrap and mono-bootstrap, and fronted by
  // gateways/rest/supporting/campaign-gateway.yaml. `reachable` is true from that
  // change on; mock mode is unaffected by this flag either way.
  campaign: { pathKey: "campaign", prefixes: ["campaign"], reachable: true, delegable: true },
  // engagement-service is built (apis/supporting/engagement-service), deploys as its OWN
  // engagement-bootstrap (and joins mono for local runs), and is fronted by
  // gateways/rest/supporting/engagement-gateway.yaml behind its own CloudFront/WAF door.
  //
  // THE ONLY CONTEXT ON THIS LIST WITH A GENUINELY INTERNET-FACING, UNAUTHENTICATED PII SURFACE.
  // `publicPrefixes` carries real weight here, unlike partner's name-echo: /engagement/public/**
  // is reached by a customer tapping a link in a WhatsApp message, with no JWT and no
  // X-Tenant-ID, and the opaque link token is the only thing that selects a tenant (D-ME-19).
  //
  // The other two sub-paths are NOT public and are not listed as such: /engagement/internal/** is
  // the S2S mint, denied at the edge with AWS_IAM because it returns a working token, and
  // /engagement/links is the JWT-gated staff diagnostic on the greedy protected route.
  engagement: {
    pathKey: "engagement",
    prefixes: ["engagement"],
    publicPrefixes: ["engagement/public"],
    reachable: true
  },
  // commerce-service is built (apis/core/commerce-service), assembled into
  // core-bootstrap and mono-bootstrap, and fronted by
  // gateways/rest/core/commerce-gateway.yaml. `reachable` is true from that change
  // on; mock mode is unaffected by this flag either way. The greedy /commerce route
  // also carries the PoS counter surface (/commerce/counter/**, ADR-010). That half
  // is declared in `@quickbee/api-commerce` alongside the console's — separated by
  // permission (commerce_counter:transact:branch) rather than by path — and is
  // consumed by apps/pos, not by apps/commerce.
  commerce: { pathKey: "commerce", prefixes: ["commerce"], reachable: true, delegable: true },
  // marketplace-service is built (apis/core/marketplace-service), assembled into
  // core-bootstrap and mono-bootstrap, and fronted by
  // gateways/rest/core/marketplace-gateway.yaml. `reachable` is true from that
  // change on — and the flag mattered here more than anywhere else it has been
  // used: D15 specified this package at `reachable: false` with stateful
  // fixtures, the platform deleted its whole mock layer on 2026-08-20, and so
  // the package was never created at all. `apps/network`'s marketplace screens
  // carried the designed contract as local types until the service existed
  // (§0.1 item 1), which is why adopting it is a package import and a hook swap
  // per screen with no screen layout changing.
  //
  // The greedy /marketplace route carries two surfaces separated by permission
  // rather than by path, the arrangement api-commerce uses for the console and
  // the PoS counter: the operator's moderation desk under
  // network-marketplace:{read,moderate}:tenant, and the saver-facing network
  // storefront under marketplace-storefront:read:own. The storefront is
  // authenticated — a public browse is deferred (§8.4).
  marketplace: { pathKey: "marketplace", prefixes: ["marketplace"], reachable: true },
  // pulse-service is built (apis/generic/pulse-service), deploys as its own
  // pulse-bootstrap (ADR-002; joins mono via LocalPulseAssembly for local runs
  // only), and is fronted by gateways/rest/generic/pulse-gateway.yaml.
  // PLATFORM_ADMIN-only: every route enforces pulse:*:any in-service, and no
  // request carries X-Tenant-ID in the dedicated deployment.
  pulse: { pathKey: "pulse", prefixes: ["pulse"], reachable: true }
};
var CONTEXT_KEYS = Object.keys(CONTEXT_REGISTRY);
var GATEWAY_PATH_KEYS = Array.from(
  new Set(CONTEXT_KEYS.map((key) => CONTEXT_REGISTRY[key].pathKey))
).sort();
function getContextDescriptor(context) {
  const descriptor = CONTEXT_REGISTRY[context];
  if (!descriptor) {
    throw new Error(`[api-config] Unknown bounded context "${context}".`);
  }
  return descriptor;
}
function isPublicPath(context, path) {
  const publicPrefixes = getContextDescriptor(context).publicPrefixes;
  if (!publicPrefixes) return false;
  const normalised = path.replace(/^\/+/, "");
  return publicPrefixes.some(
    (prefix) => normalised === prefix || normalised.startsWith(`${prefix}/`)
  );
}
function isKnownPrefix(context, path) {
  const descriptor = getContextDescriptor(context);
  const normalised = path.replace(/^\/+/, "");
  const all = [...descriptor.prefixes, ...descriptor.publicPrefixes ?? []];
  return all.some((prefix) => normalised === prefix || normalised.startsWith(`${prefix}/`));
}
function isDelegableContext(context) {
  return CONTEXT_REGISTRY[context]?.delegable === true;
}

// src/errors.ts
var UnreachableContextError = class extends Error {
  context;
  constructor(context) {
    super(
      `[api-config] Bounded context "${context}" has no deployed API Gateway stack (CONTEXT_REGISTRY.${context}.reachable === false). Screens backed by this context cannot load until the gateway ships \u2014 see backend-api-integration-design.md \xA716 B5.`
    );
    this.name = "UnreachableContextError";
    this.context = context;
  }
};
var RuntimeConfigError = class extends Error {
  constructor(message) {
    super(`[api-config] ${message}`);
    this.name = "RuntimeConfigError";
  }
};

// src/runtimeConfig.ts
var SUPPORTED_CONFIG_VERSION = 1;
var LEGACY_KEYS = ["apiUrl", "apiBaseUrl"];
var SAME_ORIGIN = "same-origin";
var UNCONFIGURED = {
  apiOrigin: "",
  cdnOrigin: "",
  env: "development",
  configVersion: SUPPORTED_CONFIG_VERSION
};
var cached = null;
function isRecord(value) {
  return typeof value === "object" && value !== null;
}
function trimTrailingSlash(value) {
  return value.replace(/\/+$/, "");
}
function assertIsBareOrigin(apiOrigin) {
  let parsed;
  try {
    parsed = new URL(apiOrigin);
  } catch {
    throw new RuntimeConfigError(
      `window.__QB_CONFIG.apiOrigin "${apiOrigin}" is not an absolute URL (expected e.g. "https://api.thequickbee.com" or "http://localhost:8080").`
    );
  }
  if (parsed.pathname !== "/" && parsed.pathname !== "") {
    throw new RuntimeConfigError(
      `window.__QB_CONFIG.apiOrigin must be an origin only ("https://api.example.com"), not a path \u2014 received "${apiOrigin}". The /{pathKey}/api/v1 prefix is owned by CONTEXT_REGISTRY.`
    );
  }
}
function resolveSameOrigin() {
  if (typeof window === "undefined" || !window.location?.origin) {
    throw new RuntimeConfigError(
      `window.__QB_CONFIG.apiOrigin is "${SAME_ORIGIN}", which resolves to the page's own origin and so needs a browser. Nothing renders a real URL before hydration \u2014 if you are seeing this on a server render, the caller reached normaliseRuntimeConfig() directly.`
    );
  }
  return trimTrailingSlash(window.location.origin);
}
function normaliseRuntimeConfig(raw) {
  if (!isRecord(raw)) {
    throw new RuntimeConfigError("window.__QB_CONFIG is missing or not an object.");
  }
  for (const legacy of LEGACY_KEYS) {
    if (legacy in raw) {
      throw new RuntimeConfigError(
        `window.__QB_CONFIG carries the removed key "${legacy}". The only supported name is "apiOrigin" (scheme + host, no path). Rebuild/redeploy the shell \u2014 \xA75.4 rule 1.`
      );
    }
  }
  const version = raw["configVersion"];
  if (version !== SUPPORTED_CONFIG_VERSION) {
    throw new RuntimeConfigError(
      `Unsupported window.__QB_CONFIG.configVersion ${String(version)} (this build understands ${SUPPORTED_CONFIG_VERSION}).`
    );
  }
  const configured = typeof raw["apiOrigin"] === "string" ? trimTrailingSlash(raw["apiOrigin"]) : "";
  const apiOrigin = configured === SAME_ORIGIN ? resolveSameOrigin() : configured;
  if (apiOrigin) assertIsBareOrigin(apiOrigin);
  if (!apiOrigin) {
    throw new RuntimeConfigError(
      "window.__QB_CONFIG.apiOrigin is empty. Every build talks to a real backend \u2014 set QB_API_URL for this environment and rebuild the shell (\xA75.4 rule 3)."
    );
  }
  const env = typeof raw["env"] === "string" ? raw["env"] : "development";
  const config = {
    apiOrigin,
    cdnOrigin: typeof raw["cdnOrigin"] === "string" ? trimTrailingSlash(raw["cdnOrigin"]) : "",
    env,
    configVersion: SUPPORTED_CONFIG_VERSION
  };
  if (typeof raw["useGatewayPathKey"] === "boolean") {
    config.useGatewayPathKey = raw["useGatewayPathKey"];
  }
  return config;
}
function getRuntimeConfig() {
  if (cached) return cached;
  if (typeof window === "undefined") {
    return UNCONFIGURED;
  }
  const raw = window.__QB_CONFIG;
  if (raw === void 0) {
    throw new RuntimeConfigError(
      "[api-config] window.__QB_CONFIG is not set. The shell injects it in its root layout; an MFE rendered outside the shell must inject one too. There is no fixture fallback."
    );
  }
  cached = normaliseRuntimeConfig(raw);
  return cached;
}
function setRuntimeConfigForTesting(config) {
  cached = config;
}
var activeApp = "shell";
function setActiveApp(appName) {
  if (appName) activeApp = appName;
}
function getActiveApp() {
  return activeApp;
}

// src/resolve.ts
var LOCAL_HOSTNAMES = /* @__PURE__ */ new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"]);
function isLocalOrigin(origin) {
  if (!origin) return false;
  try {
    const { hostname } = new URL(origin);
    return LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith(".localhost");
  } catch {
    return false;
  }
}
function usesGatewayPathKey(config = getRuntimeConfig()) {
  return config.useGatewayPathKey ?? !isLocalOrigin(config.apiOrigin);
}
function resolveContextBaseUrl(context, config = getRuntimeConfig()) {
  if (!config.apiOrigin) {
    throw new RuntimeConfigError(
      `Cannot resolve a URL for bounded context "${context}": no apiOrigin is configured. Set QB_API_URL for this environment and rebuild.`
    );
  }
  const descriptor = getContextDescriptor(context);
  const useKey = usesGatewayPathKey(config);
  if (!descriptor.reachable && useKey) {
    throw new UnreachableContextError(context);
  }
  const origin = config.apiOrigin.replace(/\/+$/, "");
  const key = useKey ? `/${descriptor.pathKey}` : "";
  return `${origin}${key}/api/v1`;
}
function resolveContextUrl(context, path, config = getRuntimeConfig()) {
  const base = resolveContextBaseUrl(context, config);
  return `${base}/${path.replace(/^\/+/, "")}`;
}

export { CONTEXT_KEYS, CONTEXT_REGISTRY, DELEGATED_PATH_SEGMENT, GATEWAY_PATH_KEYS, RuntimeConfigError, SAME_ORIGIN, UnreachableContextError, getActiveApp, getContextDescriptor, getRuntimeConfig, isDelegableContext, isKnownPrefix, isLocalOrigin, isPublicPath, normaliseRuntimeConfig, resolveContextBaseUrl, resolveContextUrl, setActiveApp, setRuntimeConfigForTesting, usesGatewayPathKey };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map